/** What the factors behind a filed figure leave out, said where the figure is stated (Q-17, G-P7-15).
 *
 * The receipt of a figure priced from a factor that counts carbon dioxide alone has always said so.
 * The line that summed 46 of them said tCO₂e and nothing else, so the filing read as a full CO2e
 * figure. A chip beside the figure says how many of the line's figures are priced that way; the
 * explanation is behind the info control (the text diet, ADR-0212 decision 2), and the server's own
 * sentence - the receipt's, from its one home - is the chip's `title` and is printed whole in the
 * line's receipts drawer and in the issued filing's record.
 *
 * **Everything shown is the server's**: the count and the tonnes are the line's own fold, and the
 * sentence is `engine.types.CO2_ONLY_NOTE`. Nothing here decides which figures are limited.
 */

import type { ReportLine } from "../../api/queries";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { StatusChip } from "../../components/ui/StatusChip";
import { factorLimitationsOf } from "../reportsFormat";
import styles from "./FactorLimitation.module.css";

type Limitation = ReportLine["factor_limitations"][number];

/** The chip's words: what the limitation is, then how many figures it touches. */
function limitationWords(item: Limitation): string {
  const figures = `${String(item.records)} ${item.records === 1 ? "figure" : "figures"}`;
  return item.kind === "co2_only_factor" ? `CO₂ only · ${figures}` : `Limited factors · ${figures}`;
}

export function FactorLimitation({ line }: { line: ReportLine }) {
  const items = factorLimitationsOf(line);
  if (items.length === 0) return null;
  return (
    <span className={styles.row} data-testid="factor-limitation">
      {items.map((item) => (
        <StatusChip key={item.kind} tone="warn" size="sm" title={item.statement} testId="factor-limitation-chip">
          {limitationWords(item)}
        </StatusChip>
      ))}
      <InfoPopover infoKey="filings.factor-limit" />
    </span>
  );
}
